import { useState, useEffect, useRef } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  FileText,
  GraduationCap,
  UserPlus,
  CreditCard,
  BarChart3,
  PanelLeftClose,
  ChevronDown,
  ArrowLeft,
  Menu,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDemoContext } from './DemoContext'
import { useDemoData } from './useDemoData'
import { BrandMark } from '../components/BrandMark'

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

// ── Sidebar sections (role-based, no Organizations/LLM Settings) ─

interface SidebarSection {
  key: string
  label: string
  items: { path: string; label: string; icon: typeof FileText }[]
}

// ── Layout Component ────────────────────────────────────────────

export function DemoLayout() {
  const { activeUser, activeRole, demoUsersList, switchUser } = useDemoContext()
  const { examDetails } = useDemoData()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation('pages')

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('ai-grader-demo-sidebar-collapsed')
    return stored === 'true'
  })

  useEffect(() => {
    localStorage.setItem('ai-grader-demo-sidebar-collapsed', String(sidebarCollapsed))
  }, [sidebarCollapsed])

  // Mobile overlay drawer
  const [mobileOpen, setMobileOpen] = useState(false)

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // User switcher dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getRoleLabel = (role: string): string => {
    if (role === 'ADMIN') return t('demo.layout.roles.admin')
    if (role === 'ORG_MANAGER') return t('demo.layout.roles.manager')
    return t('demo.layout.roles.teacher')
  }

  const pageTitles: Record<string, string> = {
    '/demo': t('demo.layout.titles.exams'),
    '/demo/exams': t('demo.layout.titles.exams'),
    '/demo/exams/new': t('demo.layout.titles.examsNew'),
    '/demo/classes': t('demo.layout.titles.classes'),
    '/demo/members': t('demo.layout.titles.members'),
    '/demo/costs': t('demo.layout.titles.costs'),
    '/demo/analytics': t('demo.layout.titles.analytics'),
  }

  const buildSections = (currentRole: string): SidebarSection[] => {
    const dashboard: SidebarSection = {
      key: 'dashboard',
      label: t('demo.layout.sections.dashboard'),
      items: [{ path: '/demo/exams', label: t('demo.layout.items.exams'), icon: FileText }],
    }

    const managementItems = [
      { path: '/demo/classes', label: t('demo.layout.items.classes'), icon: GraduationCap },
      ...(currentRole !== 'TEACHER'
        ? [
            { path: '/demo/members', label: t('demo.layout.items.members'), icon: UserPlus },
            { path: '/demo/analytics', label: t('demo.layout.items.analytics'), icon: BarChart3 },
          ]
        : []),
    ]

    const management: SidebarSection = {
      key: 'management',
      label: t('demo.layout.sections.management'),
      items: managementItems,
    }

    const settings: SidebarSection = {
      key: 'settings',
      label: t('demo.layout.sections.settings'),
      items: [{ path: '/demo/costs', label: t('demo.layout.items.costs'), icon: CreditCard }],
    }

    if (currentRole === 'TEACHER') {
      return [dashboard, management]
    }
    return [dashboard, management, settings]
  }

  // Page title — production shows the exam name in the header on the detail page.
  let title = pageTitles[location.pathname] || t('demo.layout.sections.dashboard')
  const detailMatch = location.pathname.match(/^\/demo\/exams\/(\d+)$/)
  if (detailMatch) {
    title = examDetails[Number(detailMatch[1])]?.session_name ?? t('demo.layout.examDetail')
  }
  if (location.pathname.match(/^\/demo\/exams\/\d+\/setup$/)) title = t('demo.layout.examSetup')

  const sections = buildSections(activeRole)
  const displayName = activeUser.full_name || activeUser.email
  const initials = getInitials(displayName)
  const roleLine = getRoleLabel(activeRole)

  return (
    <div className="layout">
      {/* ── Mobile overlay backdrop ── */}
      {mobileOpen && (
        <div
          className="sidebar-mobile-backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-logo">
          <BrandMark size={32} className="sidebar-logo-mark" alt="" />
          <span className="sidebar-logo-text">{t('appName', { ns: 'common' })}</span>
        </div>

        {/* Demo badge */}
        {!sidebarCollapsed && (
          <div style={{
            padding: '4px 12px',
            margin: '0 12px 8px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--accent-copper)',
            color: '#fff',
            fontSize: '0.65rem',
            fontWeight: 700,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.05em',
            textAlign: 'center' as const,
          }}>
            {t('demo.layout.demoBadge')}
          </div>
        )}

        <nav className="sidebar-nav">
          {sections.map((section) => (
            <div key={section.key} className="sidebar-section">
              <span className="sidebar-section-label">{section.label}</span>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar-nav-item ${isActive ? 'active' : ''}`
                  }
                  title={sidebarCollapsed ? item.label : undefined}
                  onClick={() => setMobileOpen(false)}
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-toggle">
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <PanelLeftClose size={20} />
            <span>{t('demo.layout.collapse')}</span>
          </button>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className={`layout-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Header */}
        <header className="header demo-header">
          <div className="flex items-center gap-12">
            {/* Mobile hamburger */}
            <button
              className="btn btn-ghost btn-sm sidebar-mobile-toggle"
              onClick={() => setMobileOpen(true)}
              title={t('demo.layout.menuAria')}
            >
              <Menu size={20} />
            </button>
            <button
              className="demo-exit-btn"
              onClick={() => navigate('/')}
              title={t('demo.layout.backAria')}
            >
              <ArrowLeft size={16} />
              <span>{t('demo.layout.exitDemo')}</span>
            </button>
            <h1 className="header-title">{title}</h1>
          </div>

          <div className="header-right">
            <div className="user-switcher" ref={dropdownRef}>
              <button
                className="user-switcher-btn"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <div className="user-avatar">{initials}</div>
                <div className="user-info">
                  <div className="user-info-name">{displayName}</div>
                  <div className="user-info-role">{roleLine}</div>
                </div>
                <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />
              </button>

              {dropdownOpen && (
                <div className="user-dropdown">
                  {demoUsersList.map((user) => {
                    const name = user.full_name || user.email
                    return (
                      <button
                        key={user.id}
                        className={`user-dropdown-item ${user.id === activeUser.id ? 'active' : ''}`}
                        onClick={() => {
                          switchUser(user.id)
                          setDropdownOpen(false)
                        }}
                      >
                        <div className="user-avatar">{getInitials(name)}</div>
                        <div className="user-dropdown-item-info">
                          <div className="user-dropdown-item-name">{name}</div>
                          <div className="user-dropdown-item-email">
                            {user.email} — {getRoleLabel(user.user_role)}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="layout-content">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
